import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Eine Zustimmung: eine Zeile je erforderlicher Person und Seite. */
export interface PrerequisiteApproval {
  user_id: string;
  /** Welchen Kurs diese Person vertritt. */
  side: 'target' | 'source';
  state: 'pending' | 'granted' | 'rejected';
  /** Wer. Admins sehen die Meldungen aller Dozenten — ohne Namen steht da
   *  nur "ausstehend", und es sieht aus, als haette der eigene Klick nichts
   *  bewirkt, obwohl jemand anderes dran ist. */
  user_name?: string | null;
  decided_at?: string | null;
  note?: string | null;
}

/**
 * Der Zustand einer Grundlagenverknüpfung. Bewusst kein Boolean: die
 * Ausschreibung verlangt, den Freigabestatus jederzeit nachvollziehbar zu
 * zeigen — also wer zugestimmt hat, wer noch fehlt, und warum sie aus ist.
 */
export interface PrerequisiteLink {
  id: string;
  target_course_id: string;
  /** Kursname, direkt aus der Antwort — damit eine Oberflaeche ohne eigene
   *  Kursliste (z. B. die Entscheidung aus der Benachrichtigung) einen
   *  lesbaren Satz bauen kann. */
  target_course_name?: string | null;
  source_course_id: string;
  source_course_name?: string | null;
  status: 'pending' | 'active' | 'rejected' | 'revoked';
  scope_bucket_ids?: string[] | null;
  requested_at?: string | null;
  activated_at?: string | null;
  closed_at?: string | null;
  closed_reason?: string | null;
  approvals: PrerequisiteApproval[];
  granted: number;
  pending: number;
  rejected: number;
  /** Nur in der Antwort auf eine Anfrage: wie viele benachrichtigt wurden. */
  notified?: number;
  /** Wie die Zustimmung des AUFRUFENDEN steht — nicht der Gesamtstatus.
   *  `null` heisst: fuer keinen der beiden Kurse verantwortlich, darf hier
   *  also nicht entscheiden. Ohne das bot die Karte "Bestaetigen" auch dem an,
   *  der laengst zugestimmt hatte, und der Klick aenderte sichtbar nichts. */
  my_state?: 'pending' | 'granted' | 'rejected' | null;
  /** Darf der Aufrufer stellvertretend fuer alle offenen Zustimmungen
   *  entscheiden? Managerinnen und Admins duerfen das — sie sehen ohnehin die
   *  Meldungen aller Dozenten und sollen nicht danebenstehen. */
  can_override?: boolean;
}

/** Ein Kurs, wie die Suche ihn fuer die Auswahl liefert. */
export interface LinkableCourse {
  id: string;
  name: string;
  /** 'moodle', 'ilias', … — nur zur Anzeige; verknuepfen darf man alle. */
  source_type?: string | null;
  /** Ohne verantwortliche Person kann niemand zustimmen. */
  has_responsible: boolean;
}

export interface LinkableCourses {
  data: LinkableCourse[];
  /** Wie viele Kurse die Suche insgesamt trifft — `data` ist nur eine Seite.
   *  Daran erkennt die Auswahl, ob sie weiter nachladen muss. */
  total: number;
  /** Ab welcher Zeile diese Seite beginnt. Kommt zurueck, damit eine spaet
   *  eintreffende Antwort nicht an die falsche Stelle gehaengt wird. */
  offset: number;
}

@Injectable({ providedIn: 'root' })
export class PrerequisiteService {
  private baseUrl = `${environment.tutorsBackend}`;

  constructor(private http: HttpClient) {}

  /**
   * Kurse dieses Mandanten nach Namen suchen — die Auswahl fuer eine neue
   * Verknuepfung.
   *
   * Serverseitig, weil die Seiten, von denen die Karte aufgerufen wird
   * (Moodle- und ILIAS-Uebersicht), nur eine Seite ihrer Tabelle geladen
   * haben: eine Auswahl aus diesem Array bot genau diese 50 Kurse an, und
   * der Kurs auf Seite vier war nicht erreichbar. Quellen-unabhaengig —
   * Moodle-, ILIAS- und portal-eigene Kurse stehen in derselben Tabelle.
   */
  searchCourses(q: string, excludeCourseId?: string, limit = 25, offset = 0) {
    let params = new HttpParams()
      .set('limit', String(limit))
      .set('offset', String(offset));
    if (q) params = params.set('q', q);
    if (excludeCourseId) params = params.set('exclude_course_id', excludeCourseId);
    return this.http.get<LinkableCourses>(`${this.baseUrl}/courses/linkable`, {
      params,
    });
  }

  /** Alle Verknüpfungen, an denen dieser Kurs beteiligt ist — in beiden Rollen. */
  list(courseId: string) {
    return this.http
      .get<{ data: PrerequisiteLink[] }>(
        `${this.baseUrl}/courses/${courseId}/prerequisites`
      )
      .pipe(map((r) => r.data || []));
  }

  /**
   * Beantragen, dass `courseId` die Inhalte von `sourceCourseId` lesen darf.
   * `scopeBucketIds` leer lassen für alle auto-gesyncten Inhalte des Quellkurses.
   */
  request(courseId: string, sourceCourseId: string, scopeBucketIds?: string[]) {
    return this.http.post<PrerequisiteLink>(
      `${this.baseUrl}/courses/${courseId}/prerequisites`,
      {
        source_course_id: sourceCourseId,
        scope_bucket_ids: scopeBucketIds?.length ? scopeBucketIds : null,
      }
    );
  }

  /** Zustimmen oder ablehnen. Eine Ablehnung schließt die Verknüpfung sofort. */
  /** `override` beantwortet ALLE noch offenen Zustimmungen — nur Managerinnen
   *  und Admins, und nur auf ausdrueckliche Anfrage. Ohne das Flag zaehlt die
   *  Entscheidung wie bisher nur fuer die eigene Zeile. */
  decide(linkId: string, grant: boolean, note?: string, override = false) {
    return this.http.post<PrerequisiteLink>(
      `${this.baseUrl}/curriculum/prerequisites/${linkId}/decision`,
      { grant, note: note || null, override }
    );
  }

  /** Zustimmung widerrufen — das darf jede zustimmende Person, jederzeit. */
  revoke(linkId: string) {
    return this.http.delete<PrerequisiteLink>(
      `${this.baseUrl}/curriculum/prerequisites/${linkId}`
    );
  }
}
